import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { hexToHsl } from '../utils/hex-to-hsl';

export type Theme = 'light' | 'dark' | 'system';

export interface ThemeColors {
  primary?: string;
  background?: string;
  accent?: string;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  /**
   * Optional hex colors. When provided, ThemeProvider converts each to HSL
   * and injects them as inline CSS variables on `document.documentElement`,
   * overriding the defaults from `tokens.css`. Tailwind tokens consume these
   * via `hsl(var(--primary))` etc.
   */
  colors?: ThemeColors;
}

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function applyTheme(theme: Theme): 'light' | 'dark' {
  const root = window.document.documentElement;
  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
  return resolved;
}

function applyColors(colors: ThemeColors | undefined): void {
  if (typeof window === 'undefined') return;
  const root = window.document.documentElement;
  const apply = (varName: string, hex: string | undefined): void => {
    if (!hex) return;
    const hsl = hexToHsl(hex);
    if (hsl) root.style.setProperty(varName, hsl);
  };
  apply('--primary', colors?.primary);
  apply('--background', colors?.background);
  apply('--accent', colors?.accent);
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'app-theme',
  colors,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    return (window.localStorage.getItem(storageKey) as Theme | null) ?? defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => applyTheme(theme));

  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, theme);
    }
  }, [theme, storageKey]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(applyTheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  useEffect(() => {
    applyColors(colors);
  }, [colors?.primary, colors?.background, colors?.accent]);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme: setThemeState,
    toggle: () => setThemeState(resolvedTheme === 'dark' ? 'light' : 'dark'),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
